import { useState } from "react";
import axios from "axios";
import { Mail, Loader2, Check, Gift, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterSignup({ variant = "footer" }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [discountCode, setDiscountCode] = useState(null);
  const [showNameField, setShowNameField] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { 
        email: email.trim().toLowerCase(),
        first_name: firstName.trim() || null
      });

      const data = response.data;

      if (data.success) {
        setIsSuccess(true);
        if (data.discount_code) {
          setDiscountCode(data.discount_code);
          toast.success("🎁 Check your email for your 10% discount code!");
        } else {
          toast.success(data.message);
        }
      } else {
        toast.error(data.detail || data.message || "Subscription failed. Please try again.");
      }
    } catch (err) {
      console.error("Newsletter signup error:", err);
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyCode = () => {
    if (discountCode) {
      navigator.clipboard.writeText(discountCode);
      toast.success("Discount code copied!");
    }
  };

  // Success state with discount code
  if (isSuccess && discountCode) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/30 rounded-xl p-6 text-center"
        data-testid="newsletter-success"
      >
        <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
          <Gift className="h-6 w-6 text-black" />
        </div>
        <h4 className="text-white text-lg font-semibold mb-2">Welcome to the family!</h4>
        <p className="text-zinc-400 text-sm mb-4">Here's your exclusive 10% discount:</p>
        
        <button
          onClick={copyCode}
          className="bg-black border-2 border-dashed border-[#D4AF37] rounded-lg px-6 py-3 mb-4 hover:bg-[#D4AF37]/10 transition-colors group"
        >
          <code className="text-[#D4AF37] text-xl font-bold tracking-wider">{discountCode}</code>
          <p className="text-zinc-500 text-xs mt-1 group-hover:text-[#D4AF37] transition-colors">Click to copy</p>
        </button>
        
        <p className="text-zinc-500 text-xs">
          Check your email for more details • Valid for 30 days
        </p>
      </motion.div>
    );
  }

  // Success state without code (already subscribed)
  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/30 rounded-xl p-6 text-center"
      >
        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="h-6 w-6 text-white" />
        </div>
        <h4 className="text-white text-lg font-semibold mb-2">You're already subscribed!</h4>
        <p className="text-zinc-400 text-sm">Thanks for being part of the Temple of Sound family.</p>
      </motion.div>
    );
  }

  // Compact footer variant
  if (variant === "footer") {
    return (
      <div className="w-full" data-testid="newsletter-signup-form">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
            <Gift className="h-4 w-4 text-[#D4AF37]" />
          </div>
          <div>
            <h4 className="text-white text-sm font-semibold">Get 10% Off</h4>
            <p className="text-zinc-500 text-xs">Join our newsletter</p>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-2">
          <AnimatePresence>
            {showNameField && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Input
                  type="text"
                  placeholder="First name (optional)"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="bg-zinc-900/50 border-zinc-700 text-white placeholder:text-zinc-500 h-10 text-sm"
                  data-testid="newsletter-firstname-input"
                />
              </motion.div>
            )}
          </AnimatePresence>
          
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setShowNameField(true)}
              className="bg-zinc-900/50 border-zinc-700 text-white placeholder:text-zinc-500 h-10 text-sm flex-1"
              required
              data-testid="newsletter-email-input"
            />
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="bg-[#D4AF37] hover:bg-[#B8962E] text-black h-10 px-4 font-semibold"
              data-testid="newsletter-submit-btn"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
            </Button>
          </div>
        </form>
        
        <p className="text-zinc-600 text-[10px] mt-2">
          No spam. Unsubscribe anytime.
        </p>
      </div>
    );
  }

  // Full width hero variant
  return (
    <div className="w-full max-w-md mx-auto" data-testid="newsletter-signup-form">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#D4AF37]/20">
          <Gift className="h-8 w-8 text-black" />
        </div>
        <h3 className="text-white text-2xl font-bold mb-2">Get 10% Off Your First Order</h3>
        <p className="text-zinc-400">
          Join the Temple of Sound family for exclusive drops, artist news, and special offers.
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          type="text"
          placeholder="First name (optional)"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className="bg-zinc-900/50 border-zinc-700 text-white placeholder:text-zinc-500 h-12"
          data-testid="newsletter-firstname-input"
        />
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="bg-zinc-900/50 border-zinc-700 text-white placeholder:text-zinc-500 h-12"
          required
          data-testid="newsletter-email-input"
        />
        <Button 
          type="submit" 
          disabled={isSubmitting}
          className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-black h-12 font-semibold text-base"
          data-testid="newsletter-submit-btn"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Subscribing...
            </>
          ) : (
            <>
              <Mail className="h-5 w-5 mr-2" />
              Subscribe & Get 10% Off
            </>
          )}
        </Button>
      </form>
      
      <p className="text-zinc-500 text-xs text-center mt-4">
        No spam, ever. Unsubscribe anytime.
      </p>
    </div>
  );
}
